import React, { useEffect, useState } from "react";
import { collection, getDocs, query, where, orderBy } from "firebase/firestore";
import { db } from "../firebase";
import * as XLSX from "xlsx";
import Layout from "../components/Layout";
const formatDateDDMMYYYY = (dateStr) => {
  if (!dateStr) return "";
  const [y, m, d] = dateStr.split("-");
  return `${d}-${m}-${y}`;
};


const InwardList = () => {
  const today = new Date().toISOString().slice(0, 10);

  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [userMap, setUserMap] = useState({});
  const resolveUserName = (uid) => {
    return userMap?.[uid] || uid;
  };

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // 🔹 Fetch inward + sku master
  const loadData = async () => {
    setLoading(true);

    // 1️⃣ Load SKU master (for ordering)
    const skuSnap = await getDocs(query(collection(db, "skuMaster")));
    const skuOrder = skuSnap.docs
      .map(d => d.data().sku)
      .filter(Boolean);

    const skuIndex = {};
    skuOrder.forEach((s, i) => (skuIndex[s] = i));

    // 2️⃣ Load inward register for date range
    const inwardQ = query(
      collection(db, "inward_register"),
      where("inwardDate", ">=", fromDate),
      where("inwardDate", "<=", toDate),
      orderBy("inwardDate")
    );

    const inwardSnap = await getDocs(inwardQ);

    // 3️⃣ Aggregate date + sku
    const map = {};

    inwardSnap.forEach(doc => {
      const d = doc.data();
      if (!d.sku || !d.inwardDate) return;

      const key = `${d.inwardDate}__${d.sku}`;

      if (!map[key]) {
        map[key] = {
          date: d.inwardDate,
          sku: d.sku,
          quantity: 0,
          rate: d.purchaseBasicRate || 0,
          source: d.source || "",
          uploadedBy: d.uploadedBy || "",
        };
      }

      map[key].quantity += Number(d.quantity) || 0;
    });

    // 4️⃣ Final sorted rows
    const finalRows = Object.values(map).sort((a, b) => {
      if (a.date !== b.date) {
        return b.date.localeCompare(a.date);
      }
      return (skuIndex[a.sku] ?? 9999) - (skuIndex[b.sku] ?? 9999);
    });

    setRows(finalRows);
    setLoading(false);
  };

  // 🔹 Export Excel
  const exportExcel = () => {
    if (rows.length === 0) return alert("No data to export");

    const data = rows.map(r => ({
      Date: r.date,
      SKU: r.sku,
      Quantity: Number(r.quantity.toFixed(2)),
      Rate: Number((r.rate || 0).toFixed(2)),
      Source: r.source,
      UploadedBy: resolveUserName(r.uploadedBy),
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Inward");

    XLSX.writeFile(
      wb,
      `Inward_${fromDate}_to_${toDate}.xlsx`
    );
  };

  useEffect(() => {
    const loadUsers = async () => {
      const snap = await getDocs(collection(db, "users"));
      const map = {};
      snap.forEach(d => {
        const data = d.data();
        map[d.id] = data.name || data.displayName || data.email || d.id;
      });
      setUserMap(map);
    };

    loadUsers();
  }, []);


  return (
    <Layout>
      <div className="layout-content">
        {/* HEADER */}
        <div style={{ marginBottom: 40, display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
            <div>
                <h1 style={{ fontSize: 32, marginBottom: 8, fontWeight: 900, letterSpacing: "-0.02em" }}>Inward Ledger Register</h1>
                <p style={{ color: "var(--text-muted)", fontSize: 16 }}>Enterprise-grade audit trail of all warehouse stock admissions.</p>
            </div>
            <button 
                onClick={exportExcel} 
                className="btn-secondary"
                style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 24px" }}
            >
                📥 EXPORT MANIFEST
            </button>
        </div>

        {/* FILTERS CARD */}
        <div className="premium-card" style={{ padding: "24px 32px", marginBottom: 32, display: "flex", alignItems: "center", gap: 32 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <label style={{ fontSize: 11, fontWeight: 800, color: "var(--text-muted)", textTransform: "uppercase" }}>Temporal Range</label>
                <div style={{ display: "flex", alignItems: "center", gap: 8, background: "var(--surface-hover)", padding: "8px 16px", borderRadius: 12, border: "1px solid var(--border)" }}>
                    <input
                        type="date"
                        value={fromDate}
                        onChange={e => setFromDate(e.target.value)}
                        style={{ border: "none", background: "transparent", fontWeight: 700, outline: "none", color: "var(--primary)" }}
                    />
                    <span style={{ color: "var(--text-muted)", fontWeight: 800 }}>→</span>
                    <input
                        type="date"
                        value={toDate}
                        onChange={e => setToDate(e.target.value)}
                        style={{ border: "none", background: "transparent", fontWeight: 700, outline: "none", color: "var(--primary)" }}
                    />
                </div>
            </div>

            <button 
                onClick={loadData} 
                className="btn-primary"
                style={{ padding: "12px 32px", borderRadius: 12 }}
            >
                SYNCHRONIZE DATA
            </button>
        </div>

        {/* DATA GRID */}
        <div className="premium-card" style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ padding: "20px 24px", borderBottom: "1px solid var(--border)", background: "var(--surface-hover)", display: "flex", justifyContent: "space-between" }}>
            <span style={{ fontSize: 11, fontWeight: 800, color: "var(--text-muted)", textTransform: "uppercase" }}>Validated Inbound Records</span>
            {loading && <span style={{ fontSize: 11, fontWeight: 800, color: "var(--primary)" }}>QUERYING CLOUD REPOSITORY...</span>}
          </div>

          <div style={{ overflowX: "auto" }}>
            <table className="enterprise-table">
              <thead>
                <tr>
                  <th style={{ paddingLeft: 24 }}>INWARD DATE</th>
                  <th>SKU IDENTIFICATION</th>
                  <th style={{ textAlign: "center" }}>QUANTITY</th>
                  <th style={{ textAlign: "right" }}>PURCHASE RATE</th>
                  <th>SOURCE ORIGIN</th>
                  <th style={{ paddingRight: 24 }}>AUTHORIZED BY</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                    <tr>
                        <td colSpan="6" style={{ padding: 100, textAlign: "center", color: "var(--text-muted)" }}>
                            <div style={{ fontSize: 40, marginBottom: 16 }}>📊</div>
                            <div style={{ fontWeight: 700, fontSize: 16 }}>No Records Found</div>
                            <p style={{ fontSize: 13, marginTop: 4 }}>Adjust your temporal range filters to retrieve data.</p>
                        </td>
                    </tr>
                ) : (
                    rows.map((r, i) => (
                    <tr key={i}>
                        <td style={{ paddingLeft: 24 }}>
                            <div style={{ fontWeight: 800, color: "var(--text-main)" }}>{formatDateDDMMYYYY(r.date)}</div>
                        </td>
                        <td>
                            <div style={{ fontWeight: 700, color: "var(--primary)" }}>{r.sku}</div>
                        </td>
                        <td style={{ textAlign: "center", fontWeight: 800 }}>{r.quantity.toFixed(0)} <span style={{ fontSize: 10, opacity: 0.6 }}>PACKETS</span></td>
                        <td style={{ textAlign: "right", fontWeight: 700 }}>₹{(r.rate || 0).toFixed(2)}</td>
                        <td>
                            <span className="badge badge-success" style={{ background: "rgba(16, 185, 129, 0.05)", border: "1px solid rgba(16, 185, 129, 0.1)" }}>{r.source || "MANUAL"}</span>
                        </td>
                        <td style={{ paddingRight: 24, fontWeight: 600, color: "var(--text-muted)" }}>{userMap[r.uploadedBy] || r.uploadedBy}</td>
                    </tr>
                    ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default InwardList;
